import {
  PointsMaterial,
  PointsMaterialParameters,
  Shader,
  Vector3,
} from "three";

export class PointCloudMaterial extends PointsMaterial {
  private _shader: Shader;
  private _maxIterations: number;
  private _perspectiveCameraPosition: Vector3 = new Vector3();
  private _rootLength: number;
  private _tileIndex: number;

  public get shader(): Shader {
    return this._shader;
  }

  public set maxIterations(value: number) {
    this._maxIterations = value;
  }

  public get maxIterations() {
    return this._maxIterations;
  }

  public setCameraInfo(rootLength: number, perspectiveCameraPosition: Vector3) {
    this._rootLength = rootLength;
    this._perspectiveCameraPosition.copy(perspectiveCameraPosition);
  }

  constructor(
    parameters?: PointsMaterialParameters,
    materialType?: string,
    tileIndex?: number
  ) {
    super(parameters);
    this._maxIterations = 1;
    this._tileIndex = tileIndex;
    this._shader = {
      uniforms: {
        shapeMidPoints: { value: [] },
        shapeMidNormals: { value: [] },
        triangleIndices: { value: [] },
        shapeCounts: { value: [] },
        highlightColor: { value: new Vector3(1, 0, 0) },
        isSelectInsides: { value: [] },
        isDeleteds: { value: [] },
        totalNumberOfShapes: { value: 0 },
      },
      vertexShader: "",
      fragmentShader: "",
    };

    this.onBeforeCompile = (shader: Shader): void => {
      this._shader = shader;
      shader.uniforms["tileIndex"] = {
        value: this._tileIndex >= 0 ? this._tileIndex / 255.0 : 1.0,
      };

      shader.uniforms["perspectiveCameraPosition"] = {
        value: this._perspectiveCameraPosition
          ? this._perspectiveCameraPosition
          : new Vector3(0, 0, 0),
      };

      shader.uniforms["rootLength"] = {
        value: this._rootLength ? this._rootLength : 1.0,
      };

      shader.uniforms["pointScale"] = { value: 3.0 };

      shader.uniforms["shapeMidPoints"] = { value: [0.0] };
      shader.uniforms["shapeMidNormals"] = { value: [0.0] };
      shader.uniforms["triangleIndices"] = { value: [0] };
      shader.uniforms["isSelectInsides"] = { value: [false] };
      shader.uniforms["isDeleteds"] = { value: [false] };
      shader.uniforms["shapeCounts"] = { value: [0] };
      shader.uniforms["totalNumberOfShapes"] = { value: 0 };
      shader.uniforms["highlightColor"] = { value: new Vector3(1, 0, 0) };
      shader.vertexShader =
        `
    const int MAX_ITERATIONS = ${this._maxIterations};

    uniform vec3 perspectiveCameraPosition;
    uniform float rootLength;
    uniform float pointScale;
    uniform float shapeMidPoints[MAX_ITERATIONS * 9];
    uniform float shapeMidNormals[MAX_ITERATIONS * 9];
    uniform int triangleIndices[MAX_ITERATIONS];
    uniform bool isSelectInsides[MAX_ITERATIONS];
    uniform bool isDeleteds[MAX_ITERATIONS];
    uniform int shapeCounts[MAX_ITERATIONS];
    uniform int totalNumberOfShapes;
    uniform vec3 highlightColor;

    attribute vec3 indices;

    varying float pointOpacity;

    // #define USE_COLOR_ALPHA
    #define color_type_rgb

    vec3 getColor(){
        vec3 c;
        
        #ifdef color_type_rgb
            c = color;
        #elif defined color_type_indices
            c = indices;
        #endif

        return c;
    }

    bool isPointInsidePlane(vec3 n, vec3 p, vec3 point)
    {
    float constant = - dot(n, p);
    float ndistToPoint = -(dot(n,point) + constant);
    vec3 projectedPoint = point.xyz + (n.xyz * ndistToPoint);
    vec3 dir = point - projectedPoint;
    if(dot(dir,n) < 0.0)
        return false;
    return true;
    }

    bool isPointInsideTriangle(vec3 p1, vec3 n1, vec3 p2, vec3 n2, vec3 p3, vec3 n3, vec3 point){
        bool res = isPointInsidePlane(n1,p1,point);
        if(res){
            res = isPointInsidePlane(n2,p2,point);
        }
        if(res){
            res = isPointInsidePlane(n3,p3,point);
        }
        return res;
    }
            ` + shader.vertexShader;

      shader.vertexShader = shader.vertexShader.replace(
        "#include <begin_vertex>",
        `
        vColor = getColor();

        pointOpacity = 1.0;
        bool isPointDiscarded = false;   
        bool isPointSelected= false; 
        int accumulatedTriangleCount = 0;  
            
        for(int shapeIndex=0; shapeIndex< MAX_ITERATIONS; shapeIndex += 1){
        if(shapeIndex >= totalNumberOfShapes) break;  
        bool isInsideShape = false;
        if(shapeIndex - 1 >= 0){
            accumulatedTriangleCount += shapeCounts[shapeIndex - 1];
        }
        for(int j =0; j< MAX_ITERATIONS; j += 1){
            int shapeCount = shapeCounts[shapeIndex];
            if( j >= shapeCount) break;
            int i = accumulatedTriangleCount + j;
            vec3 p1 = vec3(shapeMidPoints[triangleIndices[i * 3] * 3], shapeMidPoints[triangleIndices[i * 3] * 3 + 1], shapeMidPoints[triangleIndices[i * 3] * 3 + 2]);
            vec3 n1 = vec3(shapeMidNormals[triangleIndices[i * 3] * 3], shapeMidNormals[triangleIndices[i * 3] * 3 + 1], shapeMidNormals[triangleIndices[i * 3] * 3 + 2]);

            vec3 p2 = vec3(shapeMidPoints[triangleIndices[i * 3 + 1]* 3 ], shapeMidPoints[triangleIndices[i * 3 + 1]* 3 + 1], shapeMidPoints[triangleIndices[i * 3 + 1]*3 +2]);
            vec3 n2 = vec3(shapeMidNormals[triangleIndices[i * 3 + 1]* 3 ], shapeMidNormals[triangleIndices[i * 3 + 1]* 3 + 1], shapeMidNormals[triangleIndices[i * 3 + 1]*3 +2]);

            vec3 p3 = vec3(shapeMidPoints[triangleIndices[i * 3 + 2]* 3 ], shapeMidPoints[triangleIndices[i * 3 + 2]* 3 + 1], shapeMidPoints[triangleIndices[i * 3 + 2]*3 +2]);
            vec3 n3 = vec3(shapeMidNormals[triangleIndices[i * 3 + 2]* 3 ], shapeMidNormals[triangleIndices[i * 3 + 2]* 3 + 1], shapeMidNormals[triangleIndices[i * 3 + 2]*3 +2]);
            
            bool pointInsideTriangle = isPointInsideTriangle(p1,n1,p2,n2,p3,n3, position);
            if(pointInsideTriangle){
                isInsideShape = true;
                break;
            }
        }        

        if( isSelectInsides[shapeIndex] == isInsideShape){
            if(isDeleteds[shapeIndex]){
                isPointDiscarded = true;
            }
            else{
                isPointSelected = true;
            }
            break;
        }
        }      
        if(isPointDiscarded){
            pointOpacity = 0.0; 
        }
        if(isPointSelected){
            vColor = highlightColor;
        }

        #include <begin_vertex>   
    `
      );

      shader.vertexShader = shader.vertexShader.replace(
        "gl_PointSize = size;",
        `
        bool isPerspective = isPerspectiveMatrix( projectionMatrix );

        if(isPerspective){
            float dist = distance(position, perspectiveCameraPosition);
            float clamped = clamp((dist / rootLength), 0.0, 1.0);
            float proximity = 1.0 - clamped;
            float scaled = proximity * pointScale;
            gl_PointSize = scaled * size;
        }
        else{
            gl_PointSize = size;
        }
        `
      );

      // shader.vertexShader = shader.vertexShader.replace(
      //     '#include <fog_vertex>',
      //     `
      //     #include <fog_vertex>
      //     pointOpacity = 1.0;
      //     if(isHighlightActive){
      //     if(!(isPointInside ^^ isSelectInsides)){
      //         pointOpacity = 0.0;
      //     }
      //     }
      //     `);

      shader.fragmentShader =
        `
    #define color_type_rgb;
    varying float pointOpacity;
    uniform float tileIndex;
    ` + shader.fragmentShader;

      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <logdepthbuf_fragment>",
        `
        if(pointOpacity == 0.0){
            discard;
        }

        #include <logdepthbuf_fragment> 
        `
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <premultiplied_alpha_fragment>",
        `
        #include <premultiplied_alpha_fragment>

        #ifdef color_type_indices
            gl_FragColor = vec4(vColor, tileIndex);
        #endif
        `
      );

      shader.vertexShader = shader.vertexShader.replace(
        "#define color_type_rgb",
        `#define color_type_${materialType}`
      );
      shader.vertexShader = shader.vertexShader.replace(
        "#define color_type_indices",
        `#define color_type_${materialType}`
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        "#define color_type_rgb",
        `#define color_type_${materialType}`
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        "#define color_type_indices",
        `#define color_type_${materialType}`
      );

      // console.log(shader.vertexShader);
      // console.log(shader.fragmentShader);
    };
  }
}

// #include <premultiplied_alpha_fragment>
//     gl_FragColor.a  = pointOpacity;

// shader.vertexShader = shader.vertexShader.replace(
//     '#include <fog_vertex>',
//     `
//     #include <fog_vertex>
//     if(isHighlightActive){
//     if(!(isPointInside ^^ isSelectInsides)){
//         vColor = vec4( vColor.xyz,0.0);
//     }
//     }
//     `);
// };

// if(isSelectInsides[shapeIndex] && isInsideShape){
//     isInside =  true ;
//     if(isDeleteds[shapeIndex]){
//         isInsideOfDeletedShape = true;
//     }
//     break;
// }

// if(!(isSelectInsides[shapeIndex] || isInsideShape)){
//     isOutside =  true ;
//     if(isDeleteds[shapeIndex]){
//         isOutSideOfDeletedShape = true;
//     }
//     break;
// }

// if(isOutside ){
//     if(isOutSideOfDeletedShape){
//         pointOpacity = 0.0;
//     }
//     else{
//         vColor = highlightColor;
//     }

// }
// if(isInside){
//     if(isInsideOfDeletedShape){
//         pointOpacity = 0.0;
//     }
//     else{
//         vColor = highlightColor;
//     }
// }
